// Tabla del «Libro de movimientos» de tarjetas PCF.
import { Button, ErrorState, Skeleton } from '@/kit';
import { cn } from '@/lib/cn';
import { formatDateTimeMx } from '@/lib/dateHelpers';
import { CellText, NO_CELL_VALUE } from '@/shared/patterns/DataTable';
import { storeInfo, type StoreDirectory } from '../../lib/inventoryDetail';
import type { LedgerEntry } from '../../lib/inventoryLedger';
import { DETAIL_TH, DETAIL_TD, DetailEmpty, MonoId } from './DetailCard';

type LedgerColumn = 'date' | 'store' | 'type' | 'reason' | 'qty' | 'identifier' | 'author';

// Anchos fijos: el texto largo trunca con `title` en vez de partir el renglón (DEC-47).
const LEDGER_COLUMNS: readonly {
    key: LedgerColumn;
    label: string;
    width: string;
    numeric?: boolean;
}[] = [
    { key: 'date', label: 'Fecha', width: '17%' },
    { key: 'store', label: 'Tienda', width: '18%' },
    { key: 'type', label: 'Tipo', width: '11%' },
    { key: 'reason', label: 'Motivo', width: '14%' },
    { key: 'qty', label: 'Cantidad', width: '9%', numeric: true },
    { key: 'identifier', label: 'IMEI / serie', width: '16%' },
    { key: 'author', label: 'Autor', width: '15%' },
];

/** Carga, error y paginación de un libro leído del backend. */
export interface LedgerTableState {
    isPending: boolean;
    error: unknown;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    onLoadMore: () => void;
    onRetry: () => void;
    errorTitle: string;
    errorMessage: (error: unknown) => string;
}

function LedgerCell({
    column,
    entry,
    stores,
}: {
    column: LedgerColumn;
    entry: LedgerEntry;
    stores: StoreDirectory;
}) {
    switch (column) {
        case 'date':
            return (
                <td className={cn(DETAIL_TD, 'text-[10.5px] tabular-nums text-muted-foreground')}>
                    {formatDateTimeMx(entry.occurredAt)}
                </td>
            );
        case 'store': {
            const store = storeInfo(stores, entry.storeId);
            return (
                <td className={DETAIL_TD}>
                    <CellText>{store.name}</CellText>
                    <MonoId>{store.code}</MonoId>
                </td>
            );
        }
        case 'type':
            return (
                <td className={DETAIL_TD}>
                    <CellText>{entry.type}</CellText>
                </td>
            );
        case 'reason':
            return (
                <td className={DETAIL_TD}>
                    <CellText>{entry.reason}</CellText>
                </td>
            );
        case 'qty':
            return (
                <td
                    className={cn(
                        DETAIL_TD,
                        'text-right font-bold tabular-nums',
                        entry.qty.startsWith('+') ? 'text-success' : 'text-danger',
                    )}
                >
                    {entry.qty}
                </td>
            );
        case 'identifier':
            return (
                <td className={cn(DETAIL_TD, 'font-mono text-[10.5px]')}>
                    <CellText>{entry.identifier ?? NO_CELL_VALUE}</CellText>
                </td>
            );
        case 'author':
            return (
                <td className={DETAIL_TD}>
                    <CellText>{entry.author}</CellText>
                </td>
            );
    }
}

export function LedgerTable({
    entries,
    stores,
    state,
    emptyText,
    showIdentifier = true,
}: {
    entries: readonly LedgerEntry[];
    stores: StoreDirectory;
    /** Ausente cuando las filas ya vienen leídas y no se paginan. */
    state?: LedgerTableState;
    emptyText: string;
    /** Sin piezas identificadas (tarjetas PCF) la columna IMEI / serie no aplica. */
    showIdentifier?: boolean;
}) {
    if (state?.error) {
        return (
            <ErrorState
                className="m-3"
                title={state.errorTitle}
                description={state.errorMessage(state.error)}
                onRetry={state.onRetry}
            />
        );
    }
    if (state?.isPending) {
        return (
            <div className="p-3">
                <Skeleton lines={4} />
            </div>
        );
    }
    if (entries.length === 0) return <DetailEmpty>{emptyText}</DetailEmpty>;
    const columns = LEDGER_COLUMNS.filter((c) => showIdentifier || c.key !== 'identifier');
    return (
        <>
            <div className="max-h-[588px] overflow-auto">
                <table className="w-full min-w-[640px] table-fixed border-collapse text-[11px]">
                    <colgroup>
                        {columns.map((column) => (
                            <col key={column.key} style={{ width: column.width }} />
                        ))}
                    </colgroup>
                    <thead>
                        <tr>
                            {columns.map((column) => (
                                <th
                                    key={column.key}
                                    className={cn(DETAIL_TH, column.numeric && 'text-right')}
                                >
                                    {column.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {entries.map((entry) => (
                            <tr key={entry.key}>
                                {columns.map((column) => (
                                    <LedgerCell
                                        key={column.key}
                                        column={column.key}
                                        entry={entry}
                                        stores={stores}
                                    />
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {state?.hasNextPage ? (
                <div className="flex justify-center border-t border-border/40 p-2">
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={state.isFetchingNextPage}
                        onClick={state.onLoadMore}
                    >
                        {state.isFetchingNextPage ? 'Cargando…' : 'Cargar más movimientos'}
                    </Button>
                </div>
            ) : null}
        </>
    );
}

/** «N movimiento(s)» del encabezado del libro. */
export function ledgerCountLabel(n: number): string {
    return `${String(n)} movimiento${n === 1 ? '' : 's'}`;
}
